import { useEffect, useState } from "react";
import api from "@/lib/api";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import CommandPalette from "@/components/CommandPalette";
import ApprovalRequestDialog from "@/components/ApprovalRequestDialog";
import { Settings as SettingsIcon, LogOut, Boxes, Activity, Users, FileText, SlidersHorizontal, Search, Menu, CalendarDays, Sun, Moon, CheckSquare, ChevronRight, LayoutGrid, Wrench, ShieldCheck } from "lucide-react";

function usePendingRequests() {
  const [n, setN] = useState(0);
  useEffect(() => {
    const load = () => api.get("/requests", { params: { status: "pending", limit: 1 } }).then((r) => setN(r.data.pending)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("requests-changed", load);
    return () => { clearInterval(t); window.removeEventListener("requests-changed", load); };
  }, []);
  return n;
}

// Claude tab hidden (page + /claude route kept): { to: "/claude", label: "Claude", icon: Sparkles, testid: "nav-claude" }
function useSections() {
  const { user } = useAuth();
  const [mods, setMods] = useState([]);
  useEffect(() => {
    const load = () => api.get("/crm/modules").then((r) => setMods(r.data.rows)).catch(() => {});
    load();
    window.addEventListener("modules-changed", load);
    return () => window.removeEventListener("modules-changed", load);
  }, []);
  const modItems = mods.map((m) => ({ to: `/modules/${m._id}`, label: m.name, icon: Boxes, testid: `nav-module-${m._id}` }));
  const team = ["khush@thickandthin.media", "prakhar@thickandthin.media"].includes(user?.email) ? [{ to: "/team", label: "Team activity", icon: Activity, testid: "nav-team" }] : [];
  const pending = usePendingRequests();
  const desk = { to: "/admin-desk", label: user?.role === "admin" ? "Admin desk" : "My requests", icon: ShieldCheck, testid: "nav-admin-desk", badge: pending };
  return [
    { key: "modules", label: "Modules", icon: LayoutGrid, items: [{ to: "/crm", label: "CRM", icon: Users, testid: "nav-crm" }, ...modItems, { to: "/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks" }] },
    desk,
    { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
    { to: "/forms", label: "Forms", icon: FileText, testid: "nav-forms" },
    { key: "setup", label: "Setup", icon: Wrench, items: [{ to: "/crm/setup", label: "CRM setup", icon: SlidersHorizontal, testid: "nav-crm-setup" }, { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" }, ...team] },
  ];
}

const itemCls = ({ isActive }) => `nav-item ${isActive ? "active" : ""} flex items-center gap-2 px-3 py-1.5 rounded`;

function NavGroup({ g, onNav }) {
  const loc = useLocation();
  const inside = g.items.some((i) => loc.pathname === i.to || (i.to !== "/crm" && loc.pathname.startsWith(`${i.to}/`)));
  const [open, setOpen] = useState(() => localStorage.getItem(`nav_open_${g.key}`) !== "0");
  const toggle = () => { localStorage.setItem(`nav_open_${g.key}`, open ? "0" : "1"); setOpen(!open); };
  const show = open || inside;
  return (
    <div>
      <button type="button" onClick={toggle} data-testid={`nav-group-${g.key}`} aria-expanded={show}
        className={`nav-item w-full flex items-center gap-2 px-3 py-1.5 rounded ${inside ? "text-[var(--text)]" : ""}`}>
        <g.icon size={14} strokeWidth={1.5} /><span className="flex-1 text-left">{g.label}</span>
        <ChevronRight size={13} className={`transition-transform ${show ? "rotate-90" : ""}`} />
      </button>
      {show && (
        <nav className="flex flex-col gap-0.5 mt-0.5 ml-3 pl-2 border-l border-[var(--border)]" data-testid={`nav-group-items-${g.key}`}>
          {g.items.map(({ to, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} end onClick={onNav} data-testid={testid} className={itemCls}>
              <Icon size={14} strokeWidth={1.5} /><span className="truncate">{label}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}

function ThemeToggle() {
  const [light, setLight] = useState(document.documentElement.classList.contains("light"));
  useEffect(() => {
    const sync = () => setLight(document.documentElement.classList.contains("light"));
    window.addEventListener("theme-changed", sync);
    return () => window.removeEventListener("theme-changed", sync);
  }, []);
  const flip = () => {
    const on = !document.documentElement.classList.contains("light");
    document.documentElement.classList.toggle("light", on);
    localStorage.setItem("tt_theme", on ? "light" : "dark");
    window.dispatchEvent(new Event("theme-changed"));
  };
  return (
    <button onClick={flip} data-testid="theme-toggle" className="btn-ghost p-1 rounded" title={light ? "Switch to dark" : "Switch to light"}>
      {light ? <Moon size={14} strokeWidth={1.5} /> : <Sun size={14} strokeWidth={1.5} />}
    </button>
  );
}

function SideNav({ onNav, onSearch }) {
  const { user, logout } = useAuth();
  const sections = useSections();
  return (
    <div className="flex flex-col h-full p-3">
      <NavLink to="/crm" onClick={onNav} className="px-2 py-3 mb-1" data-testid="app-logo">
        <img src="/tt-logo.webp" alt="Thick & Thin" className="h-6 w-auto logo-img" />
        <div className="text-neutral-500 text-[10px] mt-1.5 tracking-wider uppercase">sales ops</div>
      </NavLink>
      <button onClick={onSearch} data-testid="open-cmdk-btn"
        className="flex items-center gap-2 px-3 py-1.5 mb-3 rounded border border-[var(--border)] text-xs text-neutral-500 hover:text-white hover:border-[var(--border-strong)] transition-colors">
        <Search size={13} />Search<span className="ml-auto kbd">⌘K</span>
      </button>
      <nav className="flex flex-col gap-0.5">
        {sections.map((s) => s.items ? <NavGroup key={s.key} g={s} onNav={onNav} /> : (
          <NavLink key={s.to} to={s.to} end onClick={onNav} data-testid={s.testid} className={itemCls}>
            <s.icon size={14} strokeWidth={1.5} /><span className="flex-1">{s.label}</span>
            {s.badge ? <span className="px-1.5 rounded bg-[var(--warn)] text-black text-[10px] font-semibold" data-testid={`${s.testid}-badge`}>{s.badge}</span> : null}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto border-t pt-3 px-2 flex items-center gap-2 text-[11px] text-neutral-500">
        <div className="flex-1 truncate">
          <div className="text-neutral-300 truncate" data-testid="user-email">{user?.email}</div>
          <div className="truncate uppercase tracking-wider text-[9px]">{user?.role === "admin" ? "admin" : "team member"}</div>
        </div>
        <ThemeToggle />
        <button onClick={logout} data-testid="logout-btn" className="btn-ghost p-1" title="Sign out"><LogOut size={14} strokeWidth={1.5} /></button>
      </div>
    </div>
  );
}

export default function Layout() {
  useEffect(() => {
    document.documentElement.classList.add("soft");
    document.documentElement.classList.toggle("light", localStorage.getItem("tt_theme") === "light");
    return () => document.documentElement.classList.remove("light", "soft");
  }, []);
  const [mobile, setMobile] = useState(false);
  const [cmdk, setCmdk] = useState(false);
  return (
    <div className="min-h-screen bg-black text-neutral-200 flex" data-testid="app-shell">
      <aside className="hidden md:block w-56 shrink-0 border-r border-[var(--border)] sticky top-0 h-screen keep-dark" data-testid="sidebar">
        <SideNav onSearch={() => setCmdk(true)} />
      </aside>
      <Sheet open={mobile} onOpenChange={setMobile}>
        <SheetContent side="left" className="w-64 p-0 bg-black border-[var(--border-strong)] keep-dark" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SideNav onNav={() => setMobile(false)} onSearch={() => { setMobile(false); setCmdk(true); }} />
        </SheetContent>
      </Sheet>
      <main className="flex-1 min-w-0" data-testid="main">
        <div className="md:hidden flex items-center gap-3 px-4 h-12 border-b border-[var(--border)] sticky top-0 bg-black/90 backdrop-blur z-30">
          <button className="btn-ghost p-1.5 -ml-1.5" onClick={() => setMobile(true)} data-testid="mobile-menu-btn"><Menu size={18} /></button>
          <img src="/tt-logo.webp" alt="Thick & Thin" className="h-5 w-auto logo-img" data-testid="mobile-logo" />
          <button className="btn-ghost p-1.5 ml-auto" onClick={() => setCmdk(true)} data-testid="mobile-search-btn"><Search size={16} /></button>
        </div>
        <Outlet />
      </main>
      <CommandPalette open={cmdk} setOpen={setCmdk} />
      <ApprovalRequestDialog />
    </div>
  );
}
